import { SecondaryButton } from '@/srp/Button';
import {
  useFinanceInfo,
  useHasExpiredSoftPull,
  useHasSoftPull
} from '~/context/srp/FinanceAndTradeContext';
import {
  SoftPullLocations,
  useOpenSoftPullModal
} from '~/hooks/srp/useToggleSoftPullModal';
import HandShakeIcon from '~/icons/srp/HandShake';
import formatAndDropDecimal from '~/utilities/srp/formatAndDropDecimal';
import roundApr from '~/utilities/srp/roundApr';
import {
  BodyWrapper,
  Bold,
  Card,
  Dot,
  FinanceInfoWrapper
} from './FinananceCard.styles';
import MinDownTooltip from './MinDownTooltip';

const getBodyText = (
  hasValidSoftPull: boolean,
  hasExpiredSoftPull: boolean
) => {
  if (hasValidSoftPull) {
    return 'You are pre-qualified';
  }

  if (hasExpiredSoftPull) {
    return (
      <>
        <Bold>Your personalized terms expired.</Bold> Get pre-qualified to see
        your actual monthly payment.
      </>
    );
  }

  return (
    <>
      These are <Bold>estimated terms.</Bold> Get pre-qualified to see your
      actual monthly payment.
    </>
  );
};

export default function FinancingCard() {
  const { maxTerm = 0, minDownPayment, startApr = 0 } = useFinanceInfo();
  const hasValidSoftPull = useHasSoftPull();
  const hasExpiredSoftPull = useHasExpiredSoftPull();
  const bodyText = getBodyText(hasValidSoftPull, hasExpiredSoftPull);
  const apr = roundApr(startApr);

  const openSoftPullModal = useOpenSoftPullModal(
    SoftPullLocations.PriceAndPayment
  );

  return (
    <Card
      data-qa={
        hasValidSoftPull
          ? 'preapproved-financing-card'
          : 'anonymous-financing-card'
      }
    >
      <div className="tl-header-s">
        {hasValidSoftPull
          ? 'Your Personalized Financing Terms'
          : 'Get Pre-Qualified'}
      </div>
      <div className="flex gap-16">
        <HandShakeIcon className="min-w-[32px]" />
        <BodyWrapper
          className={hasValidSoftPull ? 'text-blue-6' : 'text-grey-5'}
        >
          {bodyText}
        </BodyWrapper>
      </div>
      {hasValidSoftPull ? (
        <div>
          <FinanceInfoWrapper>
            <Dot />
            <div>
              Minimum cash down:{' '}
              <Bold>${formatAndDropDecimal(minDownPayment)}</Bold>
            </div>
            <MinDownTooltip apr={apr} maxTerm={maxTerm} />
          </FinanceInfoWrapper>
        </div>
      ) : (
        <SecondaryButton
          className="!min-h-[40px] w-full"
          onClick={() => openSoftPullModal()}
          data-analytics-id={null}
          data-qa="get-my-terms-link"
        >
          {hasExpiredSoftPull ? 'Get My Terms' : 'Get Pre-Qualified'}
        </SecondaryButton>
      )}
    </Card>
  );
}
